import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';

import { useDataGridTextFilter } from '../DataGrid/filters/useDataGridTextFilter.jsx';
import { useStudyProgramsDataGridFilter } from '../DataGrid/filters/DataGridFilters.jsx';
import { dataGridTextSorter } from '../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../DataGrid/DataGrid.jsx';
import { Spinner } from '../index.jsx';
import { IngProjectsTableSettings } from './IngProjectsTableSettings.jsx';

const IngProjectsTable = ({
  projects,
  isArchived = false,
  isWaiting = false,
  isAdmin = false,
  isStudent = false,
  isPanelStory = false,
  hideStatusColumn = false,
  canModifyProjects = true,
  canApply = true,
  applyDisabledTooltip,
  editDisabledTooltip,
  onRowClick,
  onEditClick,
  onDelete,
  onDetailClick,
  onStudentsClick,
  onArchiveClick,
  onActivateClick,
  canActivateClick,
  onApplyClick,
  onApproveClick,
  onRejectClick,
  onPanelStoryClick,
  panelStoryDisabled,
  panelStoryDisabledTooltip,
  secondaryCondition,
  optionsWidth = 220,
}) => {
  const { t } = useTranslation();

  const nameTextFilterProps = useDataGridTextFilter('name');
  const studyProgramsFilterProps = useStudyProgramsDataGridFilter(
    'study_programs',
    projects,
  );

  // Helper to get guarantor name
  const getGuarantorName = (project) => {
    const teachers = project.teachers || [];
    const guarantor = teachers.find(
      (teacher) => teacher.pivot?.is_guarantor === 'Áno',
    );
    return guarantor?.person?.full_name || '–';
  };

  const guarantorTextFilterProps = useDataGridTextFilter(
    'teachers',
    (teachers) => {
      if (!Array.isArray(teachers)) return '';
      const guarantor = teachers.find((t) => t.pivot?.is_guarantor === 'Áno');
      return guarantor?.person?.full_name || '';
    },
  );

  // Helper to get study program shortcuts
  const getStudyProgramShortcuts = (project) => {
    const programs = project.study_programs || [];
    if (programs.length === 0) return '–';
    return programs.map((sp) => sp.shortcut).join(', ');
  };

  const columns = useMemo(
    () => [
      {
        title: t('Názov projektu'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        ...nameTextFilterProps,
      },
      {
        title: t('Št. programy'),
        dataIndex: 'study_programs',
        width: '10%',
        ellipsis: true,

        render: (_, row) => getStudyProgramShortcuts(row),
        ...studyProgramsFilterProps,
      },
      {
        title: t('Garant'),
        dataIndex: 'teachers',
        width: '25%',
        ellipsis: true,
        render: (_, row) => getGuarantorName(row),
        ...guarantorTextFilterProps,
      },
      {
        title: t('Študenti'),
        dataIndex: 'min_students',
        width: '10%',
        sorter: (a, b) => (a.min_students || 0) - (b.min_students || 0),
        render: (_, row) => `${row.min_students || 0}-${row.max_students || 0}`,
      },
      // Hide Status column for students and panel story view
      ...(!isStudent && !isPanelStory && !hideStatusColumn
        ? [
            {
              title: t('Stav'),
              dataIndex: 'archived_at',
              ellipsis: true,
              render: (_, row) => {
                if (row.archived_at) {
                  return <Tag color="blue">{t('Archivovaný')}</Tag>;
                }

                const studyPrograms = row.study_programs || [];
                const totalPrograms = studyPrograms.length;
                const WaitingForVoteCount = studyPrograms.filter(
                  (sp) => sp.pivot?.approved === null,
                ).length;
                const declinedCount = studyPrograms.filter(
                  (sp) => sp.pivot?.approved === 'Nie',
                ).length;
                const approvedCount = studyPrograms.filter(
                  (sp) => sp.pivot?.approved === 'Áno',
                ).length;

                if (totalPrograms === WaitingForVoteCount) {
                  return <Tag color="orange">{t('Čaká na schválenie')}</Tag>;
                } else if (WaitingForVoteCount > 0) {
                  return (
                    <Tag color="orange">
                      {totalPrograms - WaitingForVoteCount}/{totalPrograms}{' '}
                      {t('Čaká na schválenie')}
                    </Tag>
                  );
                } else if (declinedCount === totalPrograms) {
                  return <Tag color="red">{t('Neschválený')}</Tag>;
                } else if (approvedCount === totalPrograms) {
                  return <Tag color="green">{t('Aktívny')}</Tag>;
                }
              },
            },
          ]
        : []),
      // Panel Story status column
      ...(isPanelStory
        ? [
            {
              title: t('Stav'),
              dataIndex: 'has_panel_story',
              filters: [
                { text: t('Vyplnený'), value: true },
                { text: t('Nevyplnený'), value: false },
              ],
              onFilter: (value, record) => !!record.has_panel_story === value,
              render: (hasPanelStory) =>
                hasPanelStory ? (
                  <Tag color="green">{t('Vyplnený')}</Tag>
                ) : (
                  <Tag color="orange">{t('Nevyplnený')}</Tag>
                ),
            },
          ]
        : []),
    ],
    [
      t,
      nameTextFilterProps,
      studyProgramsFilterProps,
      isStudent,
      isPanelStory,
      hideStatusColumn,
      guarantorTextFilterProps,
    ],
  );

  if (!projects) {
    return <Spinner />;
  }

  return (
    <DataGrid
      rowKey={(project) => project.id}
      columns={columns}
      dataSource={projects || []}
      onRowClick={onRowClick}
      onEditClick={onEditClick}
      onDelete={onDelete}
      secondaryCondition={secondaryCondition}
      deleteCondition={() => canModifyProjects}
      editDisabledTooltip={editDisabledTooltip}
      deleteDisabledTooltip={t(
        'Mazanie projektov nie je v tomto období povolené',
      )}
      optionsWidth={optionsWidth}
      customSettingsPosition="after"
      customSettings={(project) => (
        <IngProjectsTableSettings
          project={project}
          isArchived={isArchived}
          isWaiting={isWaiting}
          isAdmin={isAdmin}
          isStudent={isStudent}
          canApply={canApply}
          applyDisabledTooltip={applyDisabledTooltip}
          onDetailClick={onDetailClick}
          onStudentsClick={onStudentsClick}
          onArchiveClick={onArchiveClick}
          onActivateClick={onActivateClick}
          canActivateClick={canActivateClick}
          onApplyClick={onApplyClick}
          onApproveClick={onApproveClick}
          onRejectClick={onRejectClick}
          onPanelStoryClick={onPanelStoryClick}
          panelStoryDisabled={panelStoryDisabled}
          panelStoryDisabledTooltip={panelStoryDisabledTooltip}
        />
      )}
    />
  );
};

IngProjectsTable.propTypes = {
  projects: PropTypes.array,
  isArchived: PropTypes.bool,
  isWaiting: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isStudent: PropTypes.bool,
  isPanelStory: PropTypes.bool,
  hideStatusColumn: PropTypes.bool,
  canModifyProjects: PropTypes.bool,
  canApply: PropTypes.bool,
  applyDisabledTooltip: PropTypes.string,
  onRowClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onStudentsClick: PropTypes.func,
  onArchiveClick: PropTypes.func,
  onActivateClick: PropTypes.func,
  canActivateClick: PropTypes.func,
  onApplyClick: PropTypes.func,
  onApproveClick: PropTypes.func,
  onRejectClick: PropTypes.func,
  onPanelStoryClick: PropTypes.func,
  panelStoryDisabled: PropTypes.func,
  panelStoryDisabledTooltip: PropTypes.string,
  optionsWidth: PropTypes.number,
};

export default IngProjectsTable;
